import { useEffect, useState } from 'react';
import { createModuleLogger } from '../utils/logger';
import type { MeetingInfo } from '../types/meeting';
import type { MeetingService } from '../services/meetingService';

const log = createModuleLogger('UseMeeting');

export type MeetingLoadState =
  | { status: 'loading' }
  | { status: 'ready'; meeting: MeetingInfo }
  | { status: 'unavailable'; message: string }
  | { status: 'error'; message: string };

const missingMeetingMessage = 'No Outlook meeting is currently available.';
const incompleteMeetingMessage = 'This Outlook meeting does not have complete timing details.';
const meetingReadErrorMessage = 'We could not read meeting details from Outlook.';

function hasValidStart(meeting: MeetingInfo): boolean {
  return meeting.start instanceof Date && !Number.isNaN(meeting.start.getTime());
}

function hasValidEnd(meeting: MeetingInfo): boolean {
  return meeting.end instanceof Date && !Number.isNaN(meeting.end.getTime());
}

export function useMeeting(service: MeetingService): MeetingLoadState {
  const [state, setState] = useState<MeetingLoadState>({ status: 'loading' });

  useEffect(() => {
    let isCurrent = true;
    let requestId = 0;

    const loadMeeting = () => {
      const currentRequestId = ++requestId;

      service
        .read()
        .then((meeting) => {
          if (!isCurrent || currentRequestId !== requestId) return;

          if (!meeting) {
            log.warn('No Outlook meeting was available');
            setState({ status: 'unavailable', message: missingMeetingMessage });
            return;
          }

          if (!hasValidStart(meeting) || !hasValidEnd(meeting)) {
            log.warn('Outlook meeting has incomplete timing details');
            setState({ status: 'unavailable', message: incompleteMeetingMessage });
            return;
          }

          setState({ status: 'ready', meeting });
        })
        .catch(() => {
          if (!isCurrent || currentRequestId !== requestId) return;

          log.error('Reading the Outlook meeting failed');
          setState({ status: 'error', message: meetingReadErrorMessage });
        });
    };

    const unsubscribe = service.subscribe(loadMeeting);
    loadMeeting();

    return () => {
      isCurrent = false;
      unsubscribe();
    };
  }, [service]);

  return state;
}
